import { memo, useCallback, useContext, useMemo } from 'react';
import { ChevronRight } from 'react-feather';
import styled from '@emotion/styled';
import { createId } from '@paralleldrive/cuid2';
import type { BrandInsightsInstanceContext } from '@trinity/chat';
import { ChatActionType, ChatContextType, KolSearchStatusEnum } from '@trinity/chat';
import { useSubmitQueryMutation } from '@trinity/chat-components/components/Chat/hooks/useSubmitQueryMutation';
import { ChatStateDispatchContext } from '@trinity/chat-components/components/Chat.State/context';
import { buildDefaultChatContext } from '@trinity/chat-components/components/Chat.State/state.initial-state';
import { LandingCardIconRenderer } from '@trinity/chat-components/components/LandingCardIcon/LandingCardIconRenderer.js';
import { useNavigate } from '@trinity/chat-components/containers/Chat/RoutingProvider';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import {
  agentResponseJsonDataSchema,
  chatActionActionDataSchema,
  LandingCardActionTypeEnum,
} from '@trinity/management-app-client';
import type { ChatState } from '../Chat.State/interfaces.chat';
import { ActivePageContext, ChatContextItemsContext, ChatLandingCardsContext } from './context';
import type { LandingCard } from './interfaces.context';

interface LandingCardProps {
  title: string | null;
  description: string | null;
  icon: string | null;
  onClick: () => void;
}

interface PromptInjectionActionData {
  prompt: string;
}

interface RedirectActionData {
  url: string;
}

interface DispatchActionData {
  contextOverride?: BrandInsightsInstanceContext.ItemWithMetadata;
}

export const LandingCards = memo(() => {
  const dispatch = useContext(ChatStateDispatchContext);
  const brandIdentifier = useActiveBrandIdentifier();
  const navigate = useNavigate();
  const submitQueryMutation = useSubmitQueryMutation();
  const chatContexts = useContext(ChatContextItemsContext);
  const { setPage } = useContext(ActivePageContext);

  const cards = useContext(ChatLandingCardsContext);

  const dispatchActions = useCallback(
    (actions: ChatState.BaseAction[]) => {
      dispatch({
        type: 'batch-actions',
        payload: {
          actions: [
            ...actions,
            {
              type: 'landing-cards-visibility-toggled',
              payload: { visible: false },
            },
          ],
        },
      });
    },
    [dispatch]
  );

  const handleDispatchAction = useCallback(
    (card: LandingCard) => {
      return () => {
        if (!card.chatContext || !chatContexts) return;
        if (!brandIdentifier) {
          throw new Error('Brand identifier is required for Agent Response action');
        }

        const actionData = card.actionData as DispatchActionData;

        const fullContext = chatContexts.find((ctx) => ctx.identifier === card.chatContext?.identifier);
        if (!fullContext) return;

        // Use the same logic as Chat.Agents.Container
        const profileAgent = fullContext.agent.identifier === 'kol-profile';
        const globalAgent = fullContext.agent.identifier === 'global';

        let context: BrandInsightsInstanceContext.ItemWithMetadata = {
          type: ChatContextType.Agent,
          data: fullContext,
        };
        if (actionData?.contextOverride) {
          context = actionData.contextOverride;
        } else if (profileAgent) {
          context = buildDefaultChatContext(ChatContextType.KolProfile);
        } else if (globalAgent) {
          context = buildDefaultChatContext(ChatContextType.Global);
        }

        dispatchActions([
          {
            type: 'instance-context-changed',
            payload: {
              context,
              brandIdentifier,
            },
          },
          {
            type: 'default-context-updated',
            payload: {
              defaultContext: context,
            },
          },
        ]);

        setPage(fullContext.pageId ?? null);
      };
    },
    [dispatchActions, brandIdentifier, chatContexts, setPage]
  );

  const handlePromptInjectionAction = useCallback(
    (card: LandingCard) => {
      return () => {
        if (!card.chatContext || !chatContexts) return;

        if (!brandIdentifier) {
          throw new Error('Brand identifier is required for Agent Response action');
        }

        const fullContext = chatContexts.find((ctx) => ctx.identifier === card.chatContext?.identifier);
        if (!fullContext) return;

        const profileAgent = fullContext.agent.identifier === 'kol-profile';
        const globalAgent = fullContext.agent.identifier === 'global';

        const context: BrandInsightsInstanceContext.ItemWithMetadata = profileAgent
          ? buildDefaultChatContext(ChatContextType.KolProfile)
          : globalAgent
            ? buildDefaultChatContext(ChatContextType.Global)
            : {
                type: ChatContextType.Agent,
                data: fullContext,
              };

        dispatchActions([
          {
            type: 'instance-context-changed',
            payload: {
              context,
              brandIdentifier,
            },
          },
          {
            type: 'default-context-updated',
            payload: {
              defaultContext: context,
            },
          },
        ]);

        const promptData = card.actionData as PromptInjectionActionData;
        if (promptData?.prompt) {
          const queryIdentifier = createId();

          submitQueryMutation.mutate({
            identifier: queryIdentifier,
            parentQueryIdentifier: null,
            query: promptData.prompt,
            action: null,
            context: context,
          });
        }
      };
    },
    [dispatchActions, brandIdentifier, chatContexts, submitQueryMutation]
  );

  const handleQuestionBankClick = useCallback(() => {
    dispatchActions([
      {
        type: 'kol-search-added',
        payload: {
          item: {
            id: null,
            createdOn: new Date(),
            status: KolSearchStatusEnum.QuestionsBank,
            identifier: createId(),
            followupQuestions: [],
            form: {
              name: '',
            },
            results: null,
            profile: null,
            recommendations: {
              status: 'skipped',
              items: [],
              total: 0,
            },
            citations: [],
          },
        },
      },
    ]);
  }, [dispatchActions]);

  const handleSearchClick = useCallback(() => {
    dispatchActions([
      {
        type: 'kol-search-added',
        payload: {
          item: {
            id: null,
            createdOn: new Date(),
            status: KolSearchStatusEnum.FormEntry,
            identifier: createId(),
            followupQuestions: [],
            form: {
              name: '',
            },
            results: null,
            profile: null,
            recommendations: {
              status: 'skipped',
              items: [],
              total: 0,
            },
            citations: [],
          },
        },
      },
    ]);
  }, [dispatchActions]);

  const handleRecommendationsClick = useCallback(() => {
    dispatchActions([
      {
        type: 'kol-search-added',
        payload: {
          item: {
            id: null,
            createdOn: new Date(),
            status: KolSearchStatusEnum.ProfileRecommendations,
            identifier: createId(),
            followupQuestions: [],
            form: {
              name: '',
            },
            results: null,
            profile: null,
            recommendations: {
              status: 'uninitialized',
              items: [],
              total: 0,
            },
            citations: [],
          },
        },
      },
    ]);
  }, [dispatchActions]);

  type CustomActionData = {
    function: string;
  };

  const handleCustomAction = useCallback(
    (card: LandingCard) => {
      return () => {
        const customActionData = card.actionData as CustomActionData;
        switch (customActionData.function) {
          case '2':
            handleQuestionBankClick();
            break;
          case '3':
            handleRecommendationsClick();
            break;
          case '4':
            handleSearchClick();
            break;
          default:
        }
      };
    },
    [handleQuestionBankClick, handleRecommendationsClick, handleSearchClick]
  );

  const handleRedirectAction = useCallback(
    (card: LandingCard) => {
      return () => {
        const redirectData = card.actionData as RedirectActionData;
        //handle external vs internal links
        if (redirectData?.url) {
          if (redirectData.url.startsWith('http://') || redirectData.url.startsWith('https://')) {
            window.location.href = redirectData.url;
          } else {
            navigate(redirectData.url);
          }
        }
      };
    },
    [navigate]
  );

  const handleAgentResponseAction = useCallback(
    (card: LandingCard) => {
      return () => {
        if (!chatContexts) return;

        const agentResponseData = card.actionData;
        if (!agentResponseData) return;

        if (!brandIdentifier) {
          throw new Error('Brand identifier is required for Agent Response action');
        }

        // Parse and validate the actionData using the shared schema
        const parseResult = agentResponseJsonDataSchema.safeParse(agentResponseData);

        if (!parseResult.success) {
          return;
        }

        const { subject } = parseResult.data;

        const subjectId = Number(subject.id);

        const currentContext = chatContexts[0];
        if (!currentContext) return;

        const contextWithSubject: BrandInsightsInstanceContext.ItemWithMetadata = {
          type: ChatContextType.Agent,
          data: {
            ...currentContext,
            subject: {
              id: subjectId,
              name: subject.name,
            },
          },
        };

        // Set the context without submitting a query
        dispatch({
          type: 'batch-actions',
          payload: {
            actions: [
              {
                type: 'instance-context-changed',
                payload: {
                  context: contextWithSubject,
                  brandIdentifier,
                },
              },
              {
                type: 'default-context-updated',
                payload: {
                  defaultContext: contextWithSubject,
                },
              },
            ],
          },
        });

        setPage(currentContext.pageId ?? null);
      };
    },
    [chatContexts, dispatch, brandIdentifier, setPage]
  );

  const handleChatActionAction = useCallback(
    (card: LandingCard) => {
      return () => {
        if (!card.chatContext) return;
        if (!chatContexts) return;

        if (!brandIdentifier) {
          throw new Error('Brand identifier is required for Agent Response action');
        }

        const parseResult = chatActionActionDataSchema.safeParse(card.actionData);
        if (!parseResult.success) return;

        const actionData = parseResult.data;

        const fullContext = chatContexts.find((ctx) => ctx.identifier === card.chatContext?.identifier);
        if (!fullContext) return;

        const context: BrandInsightsInstanceContext.ItemWithMetadata = {
          type: ChatContextType.Agent,
          data: fullContext,
        };

        dispatchActions([
          {
            type: 'instance-context-changed',
            payload: {
              context,
              brandIdentifier,
            },
          },
          {
            type: 'default-context-updated',
            payload: {
              defaultContext: context,
            },
          },
        ]);

        const queryIdentifier = createId();

        submitQueryMutation.mutate({
          identifier: queryIdentifier,
          parentQueryIdentifier: null,
          query: actionData.prompt,
          action: {
            type: ChatActionType.InitiateFormWorkflow,
            payload: { workflowKey: actionData.workflowKey },
          },
          context: context,
        });
      };
    },
    [dispatchActions, brandIdentifier, chatContexts, submitQueryMutation]
  );

  const cardProps = useMemo(() => {
    if (!cards || !Array.isArray(cards)) return [];

    return cards
      .sort((a, b) => a.order - b.order)
      .map((card): LandingCardProps => {
        let onClick: () => void;

        switch (card.actionType) {
          case LandingCardActionTypeEnum.Dispatch:
            onClick = handleDispatchAction(card);
            break;
          case LandingCardActionTypeEnum.PromptInjection:
            onClick = handlePromptInjectionAction(card);
            break;
          case LandingCardActionTypeEnum.Redirect:
            onClick = handleRedirectAction(card);
            break;
          case LandingCardActionTypeEnum.PassResponseDirectlyToAgent:
            onClick = handleAgentResponseAction(card);
            break;
          case LandingCardActionTypeEnum.ChatAction:
            onClick = handleChatActionAction(card);
            break;
          default:
            onClick = handleCustomAction(card);
            break;
        }

        return {
          title: card.title,
          description: card.description,
          icon: card.icon,
          onClick,
        };
      });
  }, [
    cards,
    handleDispatchAction,
    handlePromptInjectionAction,
    handleRedirectAction,
    handleAgentResponseAction,
    handleChatActionAction,
    handleCustomAction,
  ]);

  return (
    <Root cardCount={cardProps.length}>
      {cardProps.map((card, index) => (
        <LandingCardComponent
          key={index}
          title={card.title}
          description={card.description}
          icon={card.icon}
          onClick={card.onClick}
        />
      ))}
    </Root>
  );
});

LandingCards.displayName = 'LandingCards';

const LandingCardComponent = memo(({ title, description, icon, onClick }: LandingCardProps) => {
  return (
    <CardRoot onClick={onClick}>
      <div>
        <CardHeader>
          {icon && <LandingCardIconRenderer icon={icon} alt={title || 'Card icon'} width={40} height={40} />}
        </CardHeader>

        <CardTitle>{title}</CardTitle>

        <CardCopy>{description}</CardCopy>
      </div>

      <CardFooter>
        <CardLink>
          <StyledChevronRightIcon size={20} />
        </CardLink>
      </CardFooter>
    </CardRoot>
  );
});

LandingCardComponent.displayName = 'LandingCardComponent';

const CARD_WIDTH = 258;
const GAP = 15;

const Root = styled.div<{ cardCount: number }>(({ cardCount }) => {
  const columns = cardCount === 4 ? 2 : 3;
  return {
    display: 'flex',
    flexWrap: 'wrap',
    gap: GAP,
    justifyContent: 'center',
    alignContent: 'center',
    margin: '0 auto',
    flex: 1,
    width: '100%',
    maxWidth: columns * CARD_WIDTH + (columns - 1) * GAP,
    containerType: 'inline-size',
  };
});

const CardRoot = styled.div(({ theme }) => ({
  padding: 15,
  borderRadius: 8,
  backgroundColor: theme.palette.white.main,
  border: `2px solid ${theme.palette.gray.light4}`,
  cursor: 'pointer',

  width: 258,
  height: 280,

  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  justifyContent: 'space-between',

  '@container (max-width: 530px)': {
    height: 120,
    padding: 10,
  },

  '&:hover': {
    backgroundColor: theme.isTrinityThemed
      ? theme.palette.sentiment.primary.light
      : theme.palette.btnPrimary.bg,
    border: `2px solid ${theme.isTrinityThemed ? theme.palette.sentiment.primary.main : theme.palette.primary.main}`,
  },

  [`&:hover ${CardLink}`]: {
    backgroundColor: theme.isTrinityThemed
      ? theme.palette.sentiment.primary.main
      : theme.palette.primary.main,
  },

  [`&:hover ${StyledChevronRightIcon}`]: {
    transform: 'rotate(90deg)',
    color: theme?.isTrinityThemed ? theme.palette.white.main : theme.palette?.wl?.primary?.text,
  },
}));

const CardHeader = styled.div(() => ({
  display: 'flex',
  justifyContent: 'flex-start',
  marginBottom: 10,
  '@container (max-width: 530px)': {
    marginBottom: 5,
    '& svg, & img': {
      width: '24px !important',
      height: '24px !important',
    },
  },
}));

const CardTitle = styled.div(({ theme }) => ({
  fontFamily: theme.fonts.semiBold,
  fontSize: 18,
  marginTop: 10,
  height: 63,
  '@container (max-width: 530px)': {
    fontSize: 14,
    marginTop: 5,
    height: 'auto',
  },
}));

const CardCopy = styled.div({
  '@container (max-width: 530px)': {
    display: 'none',
  },
});

const CardFooter = styled.div({
  display: 'flex',
  justifyContent: 'center',
});

const CardLink = styled.div(() => ({
  borderRadius: '50%',
  padding: 4,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
}));

const StyledChevronRightIcon = styled(ChevronRight)(({ theme }) => ({
  color: theme.isTrinityThemed ? theme.palette.sentiment.primary.main : theme.palette.primary.main,
  paddingLeft: 2,
  strokeWidth: 3,
  transition: 'transform 0.3s',
}));
